import { useCallback, useState } from "react";
import { TeamFlag } from "@/components/team-flag";

const TEAM_COLORS: Record<string, string> = {
  AUS: "#003f5c", BRA: "#2f4b7c", CAN: "#665191", DEN: "#a05195",
  ESP: "#d45087", FRA: "#f95d6a", GBR: "#ff7c43", GER: "#ffa600",
  ITA: "#488f31", NZL: "#de425b", SUI: "#374c80", SWE: "#7a5195",
  USA: "#003f5c",
};

export function teamColor(code: string): string {
  return TEAM_COLORS[code] || "#6b7280";
}

export interface ChartTooltip { x: number; y: number; text: string }

// Port (red-400) / Starboard (emerald-500) accent colors used wherever the two
// foils are shown side by side. Mirrors the PRT/STB label colors in YAW too.
export const PORT_COLOR = "#f87171";
export const STBD_COLOR = "#10b981";

// Shared row recipe for the stacked fleet list rows (ADR-042): stacks on mobile,
// collapses to a single row from the sm breakpoint, with the hover-shadow affordance.
export const FLEET_ROW_INNER =
  "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 pl-4 pr-5 py-3 transition-shadow cursor-pointer hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]";

export function useToggleSet<T>() {
  const [set, setSet] = useState<Set<T>>(new Set());
  const toggle = useCallback((v: T) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }, []);
  return [set, toggle] as const;
}

export function CheckedCard({
  checked,
  total,
  label = "Checked",
}: {
  checked: number;
  total: number;
  label?: string;
}) {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  const circumference = 2 * Math.PI * 18;
  const dashOffset = circumference - (pct / 100) * circumference;
  return (
    <div className="bg-card border-l-3 border-l-blue-400 rounded-r-lg pl-4 pr-5 py-3 flex items-center gap-4 col-span-2 sm:col-span-1">
      <div className="relative flex-shrink-0">
        <svg className="-rotate-90" width={40} height={40} viewBox="0 0 44 44">
          <circle cx={22} cy={22} r={18} fill="none" stroke="currentColor" className="text-border" strokeWidth={3} />
          <circle cx={22} cy={22} r={18} fill="none" stroke="currentColor" className="text-blue-500" strokeWidth={3}
            strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-muted-foreground">{pct}%</span>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{label}</div>
        <div className="text-lg font-bold">{checked} <span className="text-sm font-normal text-muted-foreground">/ {total}</span></div>
      </div>
    </div>
  );
}

export function ComparisonTeamPills({ codes, teamLookup, onRemove }: {
  codes: Iterable<string>;
  teamLookup: (code: string) => { team_iso_alpha2: string } | undefined;
  onRemove: (code: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {[...codes].map((code) => {
        const t = teamLookup(code);
        return (
          <button key={code} onClick={() => onRemove(code)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white hover:opacity-80 transition-opacity"
            style={{ background: teamColor(code) }}>
            {t && <TeamFlag iso_alpha2={t.team_iso_alpha2} size="xs" />}
            {code} <span className="ml-0.5 opacity-70">×</span>
          </button>
        );
      })}
    </div>
  );
}
